import { fetchApi } from "@/lib/api";
import {
  DriveEntriesQuery,
  DriveEntriesQuerySchema,
  DriveEntriesResponse,
  DriveEntriesResponseSchema,
} from "@forgepoint/shared";
import { DriveBrowserError } from "../errors";

const UNEXPECTED_RESPONSE_MESSAGE =
  "Unexpected response from the drive provider";
const HTTP_BAD_REQUEST = 400;

/**
 * Lists one page of sites, drives, or folders for a cloud drive connection.
 * @param connectionId - The drive connection to browse
 * @param query - Which level to list, plus an optional page token
 * @returns The entries and the token for the next page
 */
export async function fetchEntries(
  connectionId: string,
  query: DriveEntriesQuery,
): Promise<DriveEntriesResponse> {
  const parsedQuery = DriveEntriesQuerySchema.safeParse(query);
  if (!parsedQuery.success) {
    throw new DriveBrowserError("Invalid folder location", HTTP_BAD_REQUEST);
  }
  const params = Object.fromEntries(
    Object.entries(parsedQuery.data).filter(
      (entry): entry is [string, string] => typeof entry[1] === "string",
    ),
  );
  const response = await fetchApi(
    `connections/${connectionId}/drive/entries`,
    "GET",
    { params },
  );
  if (!response.ok) {
    throw new DriveBrowserError(
      response.error ?? "Failed to load folders",
      response.status,
    );
  }

  const parsed = DriveEntriesResponseSchema.safeParse(response.data);
  if (!parsed.success) {
    throw new DriveBrowserError(UNEXPECTED_RESPONSE_MESSAGE, response.status);
  }
  return parsed.data;
}
